import React, { useState, useEffect } from 'react';
import { X, Mail, Lock, User, Phone, MapPin, Star } from 'lucide-react';

import { useAuth } from '../context/AuthContext.tsx';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'login' | 'register';
}

export const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, mode: initialMode, onLoginSuccess }) => {
  const [mode, setMode] = useState<'login' | 'register'>(initialMode);
  const [userType, setUserType] = useState<'user' | 'mechanic'>('user');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    location: '',
    specialties: [] as string[]
  });
  /*const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');*/
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | null }>({ text: '', type: null });

  const { login } = useAuth();

  // Base URL configurable via .env
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, '') || 'http://localhost:3001';

  // Sincroniza el mode y resetea el formData cada vez que se abre el modal
  useEffect(() => {
    if (isOpen) {
      setMode(initialMode);
      setUserType('user');
      setMessage({ text: '', type: null });
      setFormData({
        name: '',
        email: '',
        password: '',
        phone: '',
        location: '',
        specialties: []
      });
    }
  }, [isOpen, initialMode]);

  const translateBackendMessage = (message: string): string => {
    if (message.includes('length must be at least 6 characters')) {
      return 'La contraseña debe tener al menos 6 caracteres';
    } else if (message.includes('email must be a valid email')) {
      return 'El correo debe tener un formato válido';
    } else if (message.includes('must be a valid email')) {
      return 'El correo electrónico debe ser válido';}
    return message;
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage({ text: '', type: null });

    const endpoint = mode === 'login' ? 'login' : 'register';

    try {
      const resp = await fetch(`${API_BASE_URL}/api/autenticar/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          identifier: formData.email,
          password: formData.password
        })
      });

      const data = await resp.json();
      console.log('🔍 Respuesta del backend:', data.message);

      const translatedMessage = translateBackendMessage(data.message || '');

      if (resp.ok && data.status === 'OK') {
        if (mode === 'login' && data.token) {
          login(data.token, data.user); 
        }
        setMessage({ text: translatedMessage, type: 'success' });
        setTimeout(() => {
          console.log('✅ Cerrando modal...');
          onClose();
          setMessage({ text: '', type: null });
        }, 1000);
      } else {
        setMessage({ text: translatedMessage, type: 'error' });
      }
    } catch (error) {
      console.error('🚨 Error de red o servidor:', error);
      setMessage({ text: 'Error de conexión con el servidor', type: 'error' });
    } finally {
      /*setLoading(false);*/
    }
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {mode === 'login' ? 'Iniciar sesión' : 'Crear cuenta'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* User Type Selection (Register only) */}
          {mode === 'register' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Tipo de cuenta
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setUserType('user')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    userType === 'user'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                  }`}
                >
                  <User className={`h-6 w-6 mx-auto mb-2 ${
                    userType === 'user'
                      ? 'text-blue-600 dark:text-blue-400'
                      : 'text-gray-500'
                  }`} />
                  <div className={`text-sm font-medium ${
                    userType === 'user'
                      ? 'text-blue-700 dark:text-blue-300'
                      : 'text-gray-700 dark:text-gray-300'
                  }`}>
                    Cliente
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setUserType('mechanic')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    userType === 'mechanic'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                  }`}
                >
                  <Star className={`h-6 w-6 mx-auto mb-2 ${
                    userType === 'mechanic'
                      ? 'text-blue-600 dark:text-blue-400'
                      : 'text-gray-500'
                  }`} />
                  <div className={`text-sm font-medium ${
                    userType === 'mechanic'
                      ? 'text-blue-700 dark:text-blue-300'
                      : 'text-gray-700 dark:text-gray-300'
                  }`}>
                    Proveedor
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Name (Register only) */}
          {mode === 'register' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Nombre completo
              </label>
              <div className="relative">
                <User className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="Tu nombre completo"
                  required
                />
              </div>
            </div>
          )}

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Correo electrónico
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="tu@email.com"
                required
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Contraseña
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
              <input
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="••••••••"
                required
              />
            </div>
          </div>

          {/* Phone (Register only) */}
          {mode === 'register' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Teléfono
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="+58 412 123 4567"
                  required
                />
              </div>
            </div>
          )}

          {/* Location (Register only) */}
          {mode === 'register' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {userType === 'mechanic' ? 'Zona de Servicio' : 'Ciudad'}
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder={userType === 'mechanic' ? 'Caracas, Miranda' : 'Tu ciudad'}
                  required
                />
              </div>
            </div>
          )}

          {message.type && (
            <div
              className={`text-sm px-4 py-3 rounded-lg ${
                message.type === 'success'
                  ? 'bg-green-100 text-green-700'
                  : 'bg-red-100 text-red-700'
              }`}
            >
              <div className="flex items-center">
                <span className="mr-2">
                  {message.type === 'success' ? '✅' : '⚠️'}
                </span>
                {message.text}
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold transition-colors"
          >
            {mode === 'login' ? 'Iniciar sesión' : 'Crear cuenta'}
          </button>

          {/* Toggle Mode */}
          <div className="text-center">
            <button
              type="button"
              onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm"
            >
              {mode === 'login' 
                ? '¿No tienes cuenta? Regístrate aquí' 
                : '¿Ya tienes cuenta? Inicia sesión'
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};